/**
 * DuplicatesDialog – Vorschlagsliste möglicher Doppel-Kunden.
 * Der Account MIT E-Mail bleibt (Ziel). Bei unklaren Fällen (beide/keine E-Mail)
 * wählt der Admin den Haupt-Datensatz selbst. Zusammenführen überträgt ALLE
 * Daten und löscht nichts.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, Mail, Phone, Merge, X, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MemberRow = ({ m, isTarget, selectable, onSelect }) => (
  <button
    type="button"
    disabled={!selectable}
    onClick={onSelect}
    className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
      isTarget ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white'
    } ${selectable ? 'hover:border-emerald-300 cursor-pointer' : 'cursor-default'}`}
    data-testid={`dup-member-${m.id}`}
  >
    {m.email ? <Mail size={14} className="text-emerald-600 shrink-0" /> : <Phone size={14} className="text-gray-400 shrink-0" />}
    <span className="font-medium text-gray-900">{m.name}</span>
    <span className="text-xs text-gray-500 truncate">{m.email || m.phone || 'keine Kontaktdaten'}</span>
    {isTarget && <Badge className="ml-auto bg-emerald-600">bleibt</Badge>}
  </button>
);

const CandidateCard = ({ cand, onMerge, onIgnore, busy }) => {
  const [chosen, setChosen] = useState(cand.target_id || null);
  const reason = cand.reason === 'phone' ? 'gleiche Telefonnummer' : 'gleicher Name';
  const canMerge = !!chosen;

  return (
    <div className="rounded-xl border border-gray-200 p-4" data-testid={`dup-candidate-${cand.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <Badge variant="outline" className="text-xs">{reason}</Badge>
        {cand.auto_mergeable
          ? <Badge className="bg-emerald-600 text-xs">automatisch erkannt</Badge>
          : <Badge className="bg-amber-500 text-xs">Haupt-Datensatz wählen</Badge>}
      </div>

      <div className="space-y-2">
        {cand.members.map((m) => (
          <MemberRow
            key={m.id}
            m={m}
            isTarget={chosen === m.id}
            selectable={!cand.auto_mergeable}
            onSelect={() => !cand.auto_mergeable && setChosen(m.id)}
          />
        ))}
      </div>

      {!cand.auto_mergeable && (
        <p className="text-xs text-gray-500 mt-2">
          Beide/kein Konto hat eine E-Mail – bitte oben den Datensatz wählen, der bestehen bleibt.
        </p>
      )}

      <div className="flex items-center gap-2 mt-3">
        <Button
          size="sm"
          disabled={!canMerge || busy}
          onClick={() => onMerge(cand, chosen)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid={`dup-merge-${cand.id}`}
        >
          {busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <Merge size={16} className="mr-1" />}
          Zusammenführen
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onIgnore(cand)}
          data-testid={`dup-ignore-${cand.id}`}
        >
          <X size={16} className="mr-1" /> Ignorieren
        </Button>
      </div>
    </div>
  );
};

export default function DuplicatesDialog({ open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [candidates, setCandidates] = useState([]);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/customers/duplicates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCandidates(res.data?.candidates || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplikate konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchCandidates();
  }, [open, fetchCandidates]);

  const handleMerge = async (cand, targetId) => {
    setBusyId(cand.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/admin/customers/duplicates/${cand.id}/merge`,
        { target_customer_id: targetId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Zusammengeführt – ${res.data?.transferred ?? 0} Einträge übertragen, nichts gelöscht.`);
      setCandidates((prev) => prev.filter((c) => c.id !== cand.id));
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zusammenführen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const handleIgnore = async (cand) => {
    setBusyId(cand.id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/customers/duplicates/${cand.id}/ignore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.info('Ignoriert – die Kunden bleiben getrennt.');
      setCandidates((prev) => prev.filter((c) => c.id !== cand.id));
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={20} /> Doppelte Kunden
          </DialogTitle>
          <DialogDescription>
            Wahrscheinliche Doppel-Logins (gleiche Telefonnummer oder gleicher Name). Der Account mit
            E-Mail bleibt bestehen; alle Termine, Zahlungen, Punkte & Rechnungen werden übertragen.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500" data-testid="dup-count">
            {loading ? 'Lade…' : `${candidates.length} Vorschlag(e)`}
          </span>
          <Button variant="ghost" size="sm" onClick={fetchCandidates} disabled={loading} data-testid="dup-refresh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin" /></div>
        ) : candidates.length === 0 ? (
          <div className="py-12 text-center text-gray-500 flex flex-col items-center gap-2">
            <CheckCircle className="text-emerald-500" size={28} />
            Keine doppelten Kunden gefunden.
          </div>
        ) : (
          <div className="space-y-3">
            {candidates.map((cand) => (
              <CandidateCard
                key={cand.id}
                cand={cand}
                busy={busyId === cand.id}
                onMerge={handleMerge}
                onIgnore={handleIgnore}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
